import { useEffect, useRef, useState } from 'react';
import { instanceKey, nextMissingTarget, outlineStops } from '../../compass/compassModel';

export function fieldElementId(formKey, fieldKey, kind) {
  if (kind === 'criterion') return `${formKey}-criterion-${fieldKey}`;
  if (kind === 'review') return `${formKey}-review-row-${fieldKey}`;
  return `${formKey}-row-${fieldKey}`;
}

export function scrollFieldIntoForm(elementId) {
  const node = document.getElementById(elementId);
  if (!node) return false;
  const scroller = node.closest('.compass-main');
  const row = node.closest('.compass-field-row, .compass-criterion') || node;
  if (!scroller) {
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    return true;
  }
  const nav = scroller.querySelector('.compass-form-nav');
  const top = row.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
  const offset = (nav?.offsetHeight || 0) + 16;
  scroller.scrollTo({ top: Math.max(0, top - offset), behavior: 'smooth' });
  return true;
}

function fieldRecord(forms, cursor) {
  if (!cursor) return null;
  return (forms.find((item) => item.formKey === cursor.formKey)?.fields || [])
    .find((item) => item.fieldKey === cursor.fieldKey) || null;
}

const ENTRY_INPUT_TYPES = new Set(['', 'text', 'number', 'email', 'tel', 'search']);

export function isEntryControl(node) {
  if (!node || node.nodeType !== 1) return false;
  if (node.tagName === 'TEXTAREA') return true;
  if (node.tagName !== 'INPUT') return false;
  const type = (node.getAttribute('type') || '').toLowerCase();
  return ENTRY_INPUT_TYPES.has(type);
}

/** The first click away from a text box stays in the guide. The second click leaves it. */
export function entryExitAction({ insideField, leavingEntry, armed }) {
  if (insideField) return { armed: false, exit: false };
  if (leavingEntry || armed) {
    if (!armed) return { armed: true, exit: false };
    return { armed: false, exit: true };
  }
  return { armed: false, exit: true };
}

function stillTyping(elementId) {
  const node = document.getElementById(elementId);
  const active = document.activeElement;
  if (!node || !active || !node.contains(active)) return false;
  return isEntryControl(active);
}

export function entryControlIn(root) {
  if (!root) return null;
  return Array.from(root.querySelectorAll('textarea, input')).find((node) => (
    isEntryControl(node) && !node.readOnly && !node.disabled
  )) || null;
}

export function focusEntryControl(elementId) {
  const entry = entryControlIn(document.getElementById(elementId));
  if (!entry) return false;
  entry.focus({ preventScroll: true });
  return true;
}

/** A locked guide leaves a finished form for the next form that still has a blank. */
export function nextFormWhenFull(forms, formKey) {
  const fields = forms.find((item) => item.formKey === formKey)?.fields || [];
  if (!formKey || fields.some((field) => field.missing)) return null;
  const target = nextMissingTarget(forms, { formKey });
  if (!target || target.formKey === formKey) return null;
  return target;
}

export function useWhatsNext({ outline, state, setState, formsFor, resumeKey = '' }) {
  const formsRef = useRef(formsFor);
  const outlineRef = useRef(outline);
  const cursorRef = useRef(null);
  const pendingScroll = useRef('');
  const exitArmed = useRef(false);
  const advanceRef = useRef(false);
  const openedKey = useRef('');
  const lockedRef = useRef(false);
  const [focusFieldKey, setFocusFieldKey] = useState('');
  const [following, setFollowing] = useState(false);
  const [locked, setLocked] = useState(false);
  const [jump, setJump] = useState(0);
  const [commitTick, setCommitTick] = useState(0);
  formsRef.current = formsFor;
  outlineRef.current = outline;
  lockedRef.current = locked;

  const highlightedNode = () => {
    const focused = cursorRef.current;
    if (!focused) return null;
    const field = fieldRecord(formsRef.current?.() || [], focused);
    return document.getElementById(fieldElementId(focused.formKey, focused.fieldKey, field?.kind));
  };

  const select = (phase, visit, form) => {
    cursorRef.current = null;
    pendingScroll.current = '';
    exitArmed.current = false;
    advanceRef.current = false;
    setFocusFieldKey('');
    if (!lockedRef.current) setFollowing(false);
    setState((current) => ({
      ...current,
      active: { phaseId: phase.id, visitId: visit?.id || phase.visitId || '', formId: form.id },
    }));
  };

  const goToNextMissing = () => {
    if (lockedRef.current) return;
    setFollowing((on) => !on);
  };

  const toggleLock = () => {
    setLocked((on) => {
      if (!on) setFollowing(true);
      return !on;
    });
  };

  useEffect(() => {
    if (!following) return undefined;
    const onFocusOut = (event) => {
      if (isEntryControl(event.target)) return;
      setCommitTick((value) => value + 1);
    };
    const onPointerDown = (event) => {
      if (lockedRef.current) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-whats-missing]')) return;
      const node = highlightedNode();
      const active = document.activeElement;
      const action = entryExitAction({
        insideField: Boolean(node && node.contains(target)),
        leavingEntry: Boolean(isEntryControl(active) && node && node.contains(active)),
        armed: exitArmed.current,
      });
      exitArmed.current = action.armed;
      if (action.exit) setFollowing(false);
    };
    const onKeyDown = (event) => {
      if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey || event.isComposing) return;
      const active = document.activeElement;
      if (!isEntryControl(active)) return;
      const node = highlightedNode();
      if (!node || !node.contains(active)) return;
      event.preventDefault();
      exitArmed.current = false;
      advanceRef.current = true;
      active.blur();
      setCommitTick((value) => value + 1);
    };
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [following]);

  useEffect(() => {
    if (!resumeKey || openedKey.current === resumeKey) return undefined;
    const forms = formsRef.current?.() || [];
    if (!forms.some((item) => item.fields?.length)) return undefined;
    openedKey.current = resumeKey;
    setFollowing(true);
    return undefined;
  }, [resumeKey]);

  useEffect(() => {
    if (!following) {
      cursorRef.current = null;
      pendingScroll.current = '';
      exitArmed.current = false;
      advanceRef.current = false;
      setFocusFieldKey((current) => (current ? '' : current));
      return undefined;
    }
    const advancing = advanceRef.current;
    advanceRef.current = false;
    const forms = formsRef.current?.() || [];
    const active = state.active || {};
    const currentKey = active.formId ? instanceKey(active.formId, active.visitId) : '';
    const focused = cursorRef.current;
    const currentField = fieldRecord(forms, focused);
    if (!advancing) {
      if (currentField?.missing && focused.formKey === currentKey) return undefined;
      const elementId = focused
        ? fieldElementId(focused.formKey, focused.fieldKey, currentField?.kind)
        : '';
      if (currentField && !currentField.missing && stillTyping(elementId)) return undefined;
      if (!lockedRef.current && exitArmed.current && focused?.formKey === currentKey) return undefined;
    }
    const hop = lockedRef.current ? nextFormWhenFull(forms, currentKey) : null;
    const target = hop || nextMissingTarget(
      forms,
      (advancing || (currentField && !currentField.missing)) ? (focused || { formKey: currentKey }) : { formKey: currentKey },
    );
    if (!target) {
      cursorRef.current = null;
      setFocusFieldKey((current) => (current ? '' : current));
      return undefined;
    }
    const elementId = fieldElementId(
      target.formKey,
      target.fieldKey,
      fieldRecord(forms, target)?.kind,
    );
    if (
      !advancing
      && currentField
      && !currentField.missing
      && stillTyping(fieldElementId(focused.formKey, focused.fieldKey, currentField.kind))
    ) {
      return undefined;
    }
    if (
      focused?.formKey === target.formKey
      && focused?.fieldKey === target.fieldKey
      && currentKey === target.formKey
    ) return undefined;
    const stop = outlineStops(outlineRef.current).find((item) => instanceKey(item.form.id, item.visitId) === target.formKey);
    if (!stop) return undefined;
    exitArmed.current = false;
    cursorRef.current = target;
    pendingScroll.current = elementId;
    setFocusFieldKey(target.fieldKey);
    setJump((value) => value + 1);
    if (currentKey !== target.formKey) {
      setState((current) => ({
        ...current,
        active: { phaseId: stop.phase.id, visitId: stop.visitId, formId: stop.form.id },
      }));
    }
    return undefined;
  }, [following, locked, state, commitTick]);

  useEffect(() => {
    const elementId = pendingScroll.current;
    if (!elementId) return undefined;
    let tries = 0;
    let frame = 0;
    const attempt = () => {
      if (scrollFieldIntoForm(elementId)) {
        focusEntryControl(elementId);
        pendingScroll.current = '';
        return;
      }
      tries += 1;
      if (tries < 12) frame = requestAnimationFrame(attempt);
    };
    frame = requestAnimationFrame(attempt);
    return () => cancelAnimationFrame(frame);
  }, [jump, state.active]);

  return { select, goToNextMissing, toggleLock, focusFieldKey, following, locked };
}
